import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { KioskCartFunnelHeaderBlockConfig } from '@front-platform/content-builder';
import { kioskCartFunnelHeaderBlockResolveConfig } from '@front-platform/content-builder';
import { combineLatest } from 'rxjs';
import { Router } from '@angular/router';
import { Component, input } from '@angular/core';
import { take, tap } from 'rxjs/operators';

import { Paths } from '@config/paths.config';
import { CartFunnelStoreSelector } from '@common/selectors/cart-funnel.selector';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { CartFunnelStoreDispatcher } from '@common/dispatchers/cart-funnel.dispatcher';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { AnalyticsService } from '@core/services/app/analytics.service';
export interface KioskCartFunnelHeaderBlockRuntimeConfig {
  'display_logo'?: boolean;
}

export type KioskCartFunnelHeaderBlockConfigInput = Partial<
  Omit<KioskCartFunnelHeaderBlockConfig, keyof KioskCartFunnelHeaderBlockRuntimeConfig> &
    KioskCartFunnelHeaderBlockRuntimeConfig
>;
export type KioskCartFunnelHeaderBlockResolvedConfig = Omit<
  KioskCartFunnelHeaderBlockConfig,
  keyof KioskCartFunnelHeaderBlockRuntimeConfig
> &
  Partial<KioskCartFunnelHeaderBlockRuntimeConfig>;
export interface KioskCartFunnelHeaderBlockContract extends ConfigComponentContract<
  KioskCartFunnelHeaderBlockConfigInput,
  KioskCartFunnelHeaderBlockResolvedConfig
> {}

@Component({
  selector: 'app-kiosk-cart-funnel-header-block',
  templateUrl: './kiosk-cart-funnel-header-block.component.html',
  standalone: false,
})
export class KioskCartFunnelHeaderBlockComponent implements KioskCartFunnelHeaderBlockContract {
  readonly config = input<KioskCartFunnelHeaderBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    KioskCartFunnelHeaderBlockConfig,
    KioskCartFunnelHeaderBlockRuntimeConfig
  >(kioskCartFunnelHeaderBlockResolveConfig, () => ({ config: this.config() }));
  get displayLogo(): boolean {
    return this.resolvedConfig()['display_logo'] as unknown as boolean;
  }
  get logo(): { url: IContentBuilderFieldImage } | undefined {
    return this.resolvedConfig()['logo'] as unknown as
      { url: IContentBuilderFieldImage } | undefined;
  }
  get backgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get backgroundImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['background_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }

  constructor(
    private readonly router: Router,
    private readonly routerHelper: RouterHelperService,
    private cartFunnelSelector: CartFunnelStoreSelector,
    private cartFunnelDispatcher: CartFunnelStoreDispatcher,
    private readonly analyticsService: AnalyticsService,
  ) {}

  back(): void {
    combineLatest([
      this.cartFunnelSelector.selectDisplayableStepsIds,
      this.cartFunnelSelector.selectActiveStepId,
    ])
      .pipe(
        take(1),
        tap(([displayableStepsIds, activeStepId]) => {
          if (!activeStepId) {
            this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
            return;
          }
          const activeStepIndex = displayableStepsIds.indexOf(activeStepId);

          if (activeStepIndex === 0) {
            this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
            return;
          }

          if (activeStepIndex === -1) {
            this.router.navigate([this.routerHelper.translateRoute(Paths.Cart)]);
            return;
          }

          this.cartFunnelDispatcher.setActive(displayableStepsIds[activeStepIndex - 1]);
        }),
      )
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(KioskCartFunnelHeaderBlockComponent, {
  'keyAliases': {},
});
